import { format } from "date-fns";
import { Result } from "effection";
import { ApiCtx, call, Next, select, sleep, spawn, takeLeading } from "starfx";

import i18n from "@app/i18n";
import { dateToSqlDate } from "@app/service/helpers";
import { EComponentStack } from "@app/state/constants";
import { isErr, isOk, runThunk, safeLast } from "@app/statefx/helpers";
import { selectCollectionByFFID } from "@app/statefx/selectors/ds-collection.sel";
import { isEmptyDataset } from "@app/statefx/workers/helpers";
import { xApi, xWorker } from "@app/statefx/xapis";
import { applog } from "@app/statefx/xThunks/app-log";
import {
  getInvoiceByPaymentSessionKey,
  immediateProvideNodeData,
} from "@app/statefx/xThunks/ds-invoice";
import { sfxNotify } from "@app/statefx/xThunks/notify";
import { setCurrentInvoiceMain } from "@app/statefx/xThunks/uiInvoiceOverall";
import { EnuAppOps } from "@app/types/insider";

import { schema } from "../schema";
// import { fxWorker } from '../workers';
import { AppState } from "../xstore";

import type { TIFCollectionItem } from "@app/types/insider";

import type { TCollectForm } from "@app/types/insider";
const t = i18n.t.bind(i18n);
function* parseableCtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx.json;
}

/**
* @description:
*  FF_ID 0 means VIRTUAL collected fiscal invoice -- via proforma only, 
*  FL_ID some means the proforma colletced id.
*  FL_ID 0 means REAL collection invoice -  fiscal only, no head, legacy. (we cashed the fiscal directly in JG)
*  FL_ID NULL means POINTED INVOICE FISCAL TO PROFORMA 1-N  (could be many collection of proformas linked to this collection of fiscal invocice)
*  --> note it is about the COLLECTION OF INVOICES not the invoice itself !!!
*  
*  The link between the invoices can or cannot mean a link between the collection of them.

*  This concept could lead to confusion.
*    
*  LINKED INVOICES is not the same as LINKED COLLECTIONS.
* @date 12/7/2023 - 11:10:53 AM
*/
const redFn = (
  acc: Record<string, TIFCollectionItem>,
  item: TIFCollectionItem
) => {
  const key = `${item.FF_ID}:${item.IFF_ID}:${item.FL_ID ?? "*"}`; //
  acc[key] = { ...item, _ID: key };
  return acc;
};

const wgetAllCollections = xWorker.get(
  "/sfx/wget/allCollections0",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const appstate = yield* select(schema.app.select);
    const start = dateToSqlDate(new Date(appstate.absolutestart));
    ctx.request = ctx.req({
      url: `/select/ivfiscal-collection-from?startDate=${start}`,
    });
    yield* next();
  }
);

const wgetSliceCollections = xWorker.get(
  "/sfx/wget/allCollections1",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const appstate = yield* select(schema.app.select);
    const start = dateToSqlDate(new Date(appstate.absolutestart));
    ctx.request = ctx.req({
      url: `/select/ivfiscal-collection-from?startDate=${start}`,
    });
    yield* next();
  }
);

// there is no benefit in reading this from worker.
// export const getInvoicePayments = xWorker.get<{ ffid: number }>(
export const getInvoicePayments = xApi.get<{ ffid: number }>(
  "/wait/collect/invoice-fiscal-collection/ffid",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.request = ctx.req({
      method: "GET",
      url: `/select/invoice-fiscal-collection?ffid=${ctx.payload.ffid}`,
    });
    ctx.cache = true;
    yield* next();
    if (isErr(ctx.result)) {
      console.log("err in ctx.result", ctx.result);
    } else {
      const data_ = yield* safeLast(parseableCtx(ctx));

      if (isErr(data_)) {
        console.log("error in data", data_);
        return;
      }

      if (isEmptyDataset(data_.value)) {
        // console.log('empty dataset');
        return;
      }

      const data = (structuredClone(data_.value) || []) as TIFCollectionItem[];

      if (isOk(data_) && data_.ok === true) {
        const items = data.reduce(
          redFn,
          {} as Record<string, TIFCollectionItem>
        );
        const existing = yield* select((s: AppState) =>
          selectCollectionByFFID(s, { ffid: ctx.payload.ffid })
        );
        const existingKeys = Object.keys(
          existing.reduce(redFn, {} as Record<string, TIFCollectionItem>)
        );
        yield* schema.update([
          schema.invoiceCollection.remove(existingKeys),
          schema.invoiceCollection.add(items),
        ]);
      }
    }
  }
);

export const processFiscalInvoicePayment = xApi.post<TCollectForm>(
  "/wait/fx/api/invoice/payment",
  { supervisor: takeLeading },
  function* (ctx, next) {
    const { payload } = ctx;
    const {
      INCASARE_ID,
      FF_ID,
      DATA_I,
      VALOARE_I,
      MONEDA_I,
      CONTMAID,
      OBS_I,
      TIP_I,
      STARE_I,
      FL_ID,
      OPTIUNE,
      OPTIUNE_FORM,
      CURS_SPECIAL,
      MONEDA_CURS_SPECIAL,
      CS_EUR,
      CS_USD,
      CS_GBP,
      CS_CHF,
    } = payload;

    const crtAmaid_ = yield* select(schema.whoami.select);
    const crtAmaid = +crtAmaid_.AmaId;

    const dataset = {
      // string build as: "FF_ID|VVALI;FF_ID|VVALI" (For example: "1|100;2|200;3|300")
      INVOICE_DATA: `${FF_ID}|${VALOARE_I};`,
      VDATAI: format(new Date(DATA_I), "yyyy-MM-dd"),
      VMONEDAI: MONEDA_I,
      VCONTI: CONTMAID,
      VOBS: OBS_I,
      VTIPI: TIP_I,
      VSTARE: STARE_I,
      OPTIUNE: OPTIUNE,
      CURS_EUR: CURS_SPECIAL,
      MONEDA_CURS: MONEDA_CURS_SPECIAL,
      CS_EUR: CS_EUR,
      CS_USD: CS_USD,
      CS_GBP: CS_GBP,
      CS_CHF: CS_CHF,
      AMA_ID: crtAmaid,
      LOGARE_ID: 0,
    };
    //  as TIFCollectionItem;

    ctx.request = ctx.req({
      method: "POST",
      url: "/select/invoice-fiscal-collection",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        credentials: "include",
      },
      body: JSON.stringify({ data: dataset }),
    });
    const currentState = yield* select(schema.uiInvoiceOverall.select);
    yield* next();
    const data = yield* safeLast(parseableCtx(ctx));
    if (isErr(data)) {
      yield* runThunk(sfxNotify, {
        type: "error",
        message: data.error?.message || t("save_collection_error"),
        duration: 5000,
      }),
        console.log("error in data", data);
      return;
    }
    const result = data.value as [{ OPRESULT: string }];
    const [{ OPRESULT }] = result;
    // what's the deal??
    // console.log('OPRESULT', OPRESULT);
    try {
      const dbResult = JSON.parse(OPRESULT);
      if (isOk(dbResult)) {
        yield* spawn(function* () {
          yield* runThunk(sfxNotify, {
            type: "success",
            message: "✓ Ok",
            duration: 10_000,
            position: "bottom",
          });
        });
        // either sessionKey or ffid, not both
        yield* call(
          getInvoiceByPaymentSessionKey.run({
            sessionKey: dbResult.value as string,
            ffid: 0,
          })
        );
        yield* call(getInvoicePayments.run({ ffid: FF_ID }));
        const invoiceBefore_ = yield* select(schema.uiInvoiceCurrent.select);
        const invoiceBefore = invoiceBefore_.currentRecord;
        // PAY ATTENTION TO THE KEY DEFINITION
        const iKey = `${invoiceBefore?.FF_ID}`;

        const currentInvoice = yield* select((s: AppState) =>
          schema.invoiceFiscal.selectById(s, { id: iKey })
        );

        if (currentInvoice && Object.keys(currentInvoice).length > 0) {
          yield* runThunk(setCurrentInvoiceMain, currentInvoice);
        }
      } else {
        yield* runThunk(sfxNotify, {
          type: "error",
          message: dbResult.error || t("save_collection_error"),
          duration: 5000,
        });
      }
      // refresh the invoice data in worker and in main
      yield* call(
        immediateProvideNodeData.run({
          nodeId: currentState.currentItem,
          includePrevious: false,
          CIF: currentState.selectedCIF,
          stack: EComponentStack.InvoiceTracking,
        })
      );

      // this will free the persistor forwarding to redis //
      yield* call(
        applog.run({ operation: EnuAppOps.collection_end_editing, value: null })
      );
    } catch (e) {
      // console.log('error in data', data);
      void 0;
    }
  }
);

export const deleteFiscalInvoicePayment = xApi.delete<TCollectForm>(
  "fx/api/invoice/payment",
  { supervisor: takeLeading },
  function* (ctx, next) {
    const crtAmaid_ = yield* select(schema.whoami.select);
    const crtAmaid = +crtAmaid_.AmaId;
    const { FF_ID, INCASARE_ID, FL_ID } = ctx.payload;
    const dataset = {
      FF_ID,
      IFF_ID: INCASARE_ID,
      FL_ID,
      AMA_ID: crtAmaid,
    };
    ctx.request = ctx.req({
      method: "DELETE",
      url: "/select/invoice-fiscal-collection",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        credentials: "include",
      },
      body: JSON.stringify({ data: dataset }),
    });
    yield* next();
    const data = yield* safeLast(parseableCtx(ctx));
    if (isErr(data)) {
      yield* runThunk(sfxNotify, {
        type: "error",
        message: data.error?.message || t("delete_collection_error"),
        duration: 5000,
      }),
        console.log("error in data", data);
      return;
    }
    const result = data.value as [{ OPRESULT: string }];
    //OPRESULT ='{"ok":true,"value":{"session":"'||:XSESSION||'"}}';
    const [{ OPRESULT }] = result;
    try {
      const dbResult = JSON.parse(OPRESULT) as Result<{ session: string }>;

      if (isOk(dbResult)) {
        yield* runThunk(sfxNotify, {
          type: "success",
          message: t("delete_collection_success"),
          duration: 3000,
        });
        // either sessionKey or ffid, not both, BUT: dbResult.value  might be '' and is nullified in prodedure
        // KARMA_URMARIRE_FISCALE_SKEY
        yield* call(
          getInvoiceByPaymentSessionKey.run({
            sessionKey: dbResult.value.session || "",
            ffid: FF_ID,
          })
        );
        yield* call(getInvoicePayments.run({ ffid: ctx.payload.FF_ID }));
      } else {
        yield* runThunk(sfxNotify, {
          type: "error",
          message: dbResult.error || t("delete_collection_error"),
          duration: 5000,
        });
      }
    } catch (e) {
      // console.log('error in data', data);
      void 0;
    }
  }
);
